/*
 * getElementComputedDimensions()
 * Vrátí výšku a šířku elementu v px
 */
interface ElementComputedDimensions {
  height: number;
  width: number;
}

// getComputedStyle vrací u elementu bez rozměrů 'auto', ne '0px' — parseInt by dal NaN
const parsePxToNumber = (value: string | null): number => {
  const parsed = parseInt(value ?? '', 10);

  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getElementComputedDimensions =(element: HTMLElement): ElementComputedDimensions => {
  if (!element) {
    return { height: 0, width: 0 };
  }

  const elementHeightInPx: string | null = window.getComputedStyle(element).height;
  const elementWidthInPx: string | null = window.getComputedStyle(element).width;

  return {
    height: parsePxToNumber(elementHeightInPx),
    width: parsePxToNumber(elementWidthInPx),
  };
};

/*
 * getElementConstantHeight()
 * Měří konstatní (stabilní) výšky elementu pomocí opakovaného měření s možností prodlevy v čase
 */

export interface GetElementHeightProps {
  el: HTMLElement;
  intervalRepetitions?: number; // Počet opakování, po kterých se výška považuje za konstantní (stabilní)
  intervalDelay?: number; // Prodleva mezi opakováními
}

export const getElementConstantHeight = async ({
  el,
  intervalRepetitions = 10,
  intervalDelay = 0,
}: GetElementHeightProps): Promise<number> =>
  new Promise((resolve, reject) => {
    if (!el) {
      reject(new Error('HTMLElement is not provided or found.'));
      return;
    }

    const elementHeightArray: number[] = [];
    let loopPosition = 0;

    const intervalId: ReturnType<typeof setInterval> = setInterval(() => {
      // Kontrola, zda je počet opakování naplněný
      const hasAllRepetions = elementHeightArray.length === intervalRepetitions;
      // Kontrola shodnosti všech výpočtů měření v průběhu opakování
      const allMeasurementsAreSame = elementHeightArray.every((val, i, arr) => val === arr[0]);

      // Stabilní výška (= naplněný počet opakování + všechny výpočty jsou stejné)
      if (
        hasAllRepetions && // Pro počáteční naplnění array
        allMeasurementsAreSame // Kontrola stejnosti všech prvků
      ) {
        clearInterval(intervalId);
        resolve(elementHeightArray[0]);
      }

      // Reset měření, pokud je počet opakování naplněný ale měření se neshodují
      if (loopPosition >= intervalRepetitions) {
        loopPosition = 0;
      }

      // Záznam měření a posunutí pozice opakování
      elementHeightArray[loopPosition] = el.offsetHeight;
      loopPosition += 1;
    }, intervalDelay);
  });

const isElementXPercentInViewport = (el: HTMLElement, percentVisible: number): boolean => {
  const rect = el.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;

  return !(
    Math.floor(100 - ((rect.top >= 0 ? 0 : rect.top) / +-rect.height) * 100) < percentVisible ||
    Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < percentVisible
  );
};

// Ověřit viditelnost prvku
export const isVisible = (element: HTMLElement, partially?: boolean): boolean => {
  const rect = element.getBoundingClientRect();
  const isSizeZero = element.offsetWidth === 0 && element.offsetHeight === 0;

  if (partially) {
    return !isSizeZero && isElementXPercentInViewport(element, 20);
  } else {
    return (
      !isSizeZero &&
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) /*or $(window).height() */ &&
      rect.right <=
        (window.innerWidth || document.documentElement.clientWidth) /*or $(window).width() */
    );
  }
};

export const getElementMaxHeight = (targetEl: HTMLElement): string => {
  const computedStyle = window.getComputedStyle(targetEl);
  return computedStyle.getPropertyValue('max-height');
};
